<template>
  <Dialog
    :visible="visible"
    @update:visible="onVisibleChange"
    :header="isEdit ? 'Редактировать задачу' : 'Новая задача'"
    :modal="true"
    :style="{ width: '560px' }"
    :breakpoints="{ '640px': '95vw' }"
    class="task-form-dialog"
  >
    <div class="form-grid">
      <div class="field">
        <label class="field-label">Название <span class="req">*</span></label>
        <InputText v-model="form.title" placeholder="Что нужно сделать" autofocus />
      </div>

      <div class="field">
        <label class="field-label">Описание</label>
        <Textarea v-model="form.description" rows="4" autoResize placeholder="Детали задачи (необязательно)" />
      </div>

      <div class="field-row">
        <div class="field">
          <label class="field-label">Срочность</label>
          <Select
            v-model="form.urgency"
            :options="urgencyOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="field">
          <label class="field-label">Срок</label>
          <DatePicker
            v-model="form.due_date"
            dateFormat="dd.mm.yy"
            showButtonBar
            placeholder="дд.мм.гггг"
          />
        </div>
      </div>

      <div class="field-row">
        <div class="field">
          <label class="field-label">Исполнитель</label>
          <Select
            v-model="form.assignee_id"
            :options="users"
            optionLabel="username"
            optionValue="id"
            placeholder="Не назначен"
            showClear
            filter
          />
        </div>
        <div class="field">
          <label class="field-label">Метка / категория</label>
          <InputText v-model="form.label" placeholder="напр. бэкенд, дизайн" />
        </div>
      </div>

      <!-- Картинки -->
      <div class="field">
        <label class="field-label">Картинки</label>
        <div class="images-zone">
          <!-- Уже сохранённые (только в режиме редактирования) -->
          <div v-for="img in existingImages" :key="'e' + img.id" class="thumb-wrap">
            <img :src="img.url" class="thumb" :alt="img.original_name || ''" />
            <button type="button" class="thumb-remove" @click="removeExisting(img)" title="Удалить">
              <i class="pi pi-times"></i>
            </button>
          </div>
          <!-- Новые (ещё не загруженные) -->
          <div v-for="(p, i) in pending" :key="'p' + i" class="thumb-wrap">
            <img :src="p.url" class="thumb" :alt="p.file.name" />
            <button type="button" class="thumb-remove" @click="removePending(i)" title="Убрать">
              <i class="pi pi-times"></i>
            </button>
          </div>
          <!-- Кнопка добавления -->
          <button type="button" class="thumb-add" @click="fileInput?.click()">
            <i class="pi pi-plus"></i>
            <span>Добавить</span>
          </button>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden-input"
            @change="onFilesPicked"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Отмена" severity="secondary" variant="text" @click="close" :disabled="saving" />
      <Button :label="isEdit ? 'Сохранить' : 'Создать'" icon="pi pi-check" @click="save" :loading="saving" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import DatePicker from 'primevue/datepicker'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import apiService from '../../services/apiService'
import { URGENCY_OPTIONS } from '../../utils/taskConstants'

const props = defineProps({
  visible: { type: Boolean, default: false },
  task: { type: Object, default: null },
  users: { type: Array, default: () => [] }
})
const emit = defineEmits(['update:visible', 'saved'])

const toast = useToast()
const urgencyOptions = URGENCY_OPTIONS

const fileInput = ref(null)
const saving = ref(false)
const existingImages = ref([])
const pending = ref([]) // [{ file, url }]

const form = reactive({
  title: '',
  description: '',
  urgency: 'medium',
  label: '',
  due_date: null,
  assignee_id: null
})

const isEdit = computed(() => !!props.task)

function resetPending() {
  pending.value.forEach((p) => URL.revokeObjectURL(p.url))
  pending.value = []
}

function initForm() {
  const t = props.task
  form.title = t?.title || ''
  form.description = t?.description || ''
  form.urgency = t?.urgency || 'medium'
  form.label = t?.label || ''
  form.due_date = t?.due_date ? new Date(t.due_date) : null
  form.assignee_id = t?.assignee_id ?? null
  existingImages.value = t?.images ? [...t.images] : []
  resetPending()
}

watch(
  () => props.visible,
  (v) => {
    if (v) initForm()
    else resetPending()
  }
)

function onVisibleChange(v) {
  if (!v) close()
}

function close() {
  emit('update:visible', false)
}

function onFilesPicked(e) {
  const files = Array.from(e.target.files || [])
  files.forEach((file) => {
    if (file.type.startsWith('image/')) {
      pending.value.push({ file, url: URL.createObjectURL(file) })
    }
  })
  e.target.value = '' // позволяем выбрать те же файлы повторно
}

function removePending(i) {
  URL.revokeObjectURL(pending.value[i].url)
  pending.value.splice(i, 1)
}

async function removeExisting(img) {
  try {
    await apiService.deleteTaskImage(props.task.id, img.id)
    existingImages.value = existingImages.value.filter((x) => x.id !== img.id)
  } catch (err) {
    console.error(err)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить картинку', life: 3000 })
  }
}

function toDateOnly(d) {
  if (!d) return null
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return null
  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

async function save() {
  if (!form.title.trim()) {
    toast.add({ severity: 'warn', summary: 'Проверьте поля', detail: 'Название задачи обязательно', life: 3000 })
    return
  }

  saving.value = true
  try {
    const payload = {
      title: form.title.trim(),
      description: form.description?.trim() || null,
      urgency: form.urgency,
      label: form.label?.trim() || null,
      due_date: toDateOnly(form.due_date),
      assignee_id: form.assignee_id ?? null
    }

    let taskId
    if (isEdit.value) {
      await apiService.updateTask(props.task.id, payload)
      taskId = props.task.id
    } else {
      const created = await apiService.createTask(payload)
      taskId = created.id
    }

    if (pending.value.length) {
      await apiService.uploadTaskImages(taskId, pending.value.map((p) => p.file))
    }

    toast.add({
      severity: 'success',
      summary: 'Готово',
      detail: isEdit.value ? 'Задача обновлена' : 'Задача создана',
      life: 2500
    })
    emit('saved')
    close()
  } catch (err) {
    console.error(err)
    const detail = err?.response?.data?.detail || 'Не удалось сохранить задачу'
    toast.add({ severity: 'error', summary: 'Ошибка', detail, life: 4000 })
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  padding-top: 0.25rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
}

.field-row {
  display: flex;
  gap: 1rem;
}

.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-color-secondary);
}

.req {
  color: var(--red-500);
}

.field :deep(.p-inputtext),
.field :deep(.p-select),
.field :deep(.p-datepicker) {
  width: 100%;
}

.images-zone {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.thumb-wrap {
  position: relative;
  width: 72px;
  height: 72px;
}

.thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--surface-300);
}

.thumb-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: var(--red-500);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  line-height: 1;
}

.thumb-add {
  width: 72px;
  height: 72px;
  border: 1px dashed var(--surface-400);
  border-radius: 8px;
  background: transparent;
  color: var(--text-color-secondary);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  font-size: 0.75rem;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.thumb-add:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.hidden-input {
  display: none;
}
</style>
